import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import {
  DomainScrapingInfo,
  KnowledgeBaseService,
  ScrapeDomainRequest,
} from "services/api-workers-service"
import { SentryService } from "services/SentryService"
import { RootState } from "store/store"
import styled from "styled-components"
import { Box, Row } from "ui/components/atoms/_atoms"
import Button, { IconButton } from "ui/components/atoms/button"
import Input from "ui/components/atoms/input"
import Spinner from "ui/components/atoms/spinner"
import { Label, P, Title } from "ui/components/atoms/typography"
import Alert from "ui/components/molecules/alert"
import { formatDate } from "utils/date"

const ScrapingCard = ({
  scraping,
  onRefresh,
  onDelete,
}: {
  scraping: DomainScrapingInfo
  onRefresh: () => void
  onDelete: () => void
}) => {
  const { t, i18n } = useTranslation(["panel"])
  const [expanded, setExpanded] = useState(false)

  return (
    <ScrapingWrapper>
      <Box $flex={1}>
        <Row $justifyContent="space-between" $alignItems="flex-start" $marginBottom="XS">
          <Box $flex={1}>
            <Label $marginBottom="XS">{scraping.domain}</Label>
            <P $small $marginBottom="XS">
              {t("profile.knowledge-base.scrapings.pages-count", {
                count: scraping.pages_count,
              })}
            </P>
            <P $small $marginBottom="0">
              {t("profile.knowledge-base.scrapings.last-scraped")}:{" "}
              {formatDate(scraping.last_scraped_at, i18n.language)}
            </P>
          </Box>
          <ActionsRow>
            <IconButton
              $size="45px"
              $borderRadius="borderRadius"
              $iconSize="M"
              $background="background"
              icon={expanded ? "arrow-top" : "arrow-bottom"}
              onClick={() => setExpanded(!expanded)}
              ariaLabel={expanded ? "Collapse" : "Expand"}
            />
            <IconButton
              $size="45px"
              $borderRadius="borderRadius"
              $iconSize="M"
              $background="background"
              icon="clock"
              onClick={onRefresh}
              ariaLabel="Refresh"
            />
            <IconButton
              $size="45px"
              $borderRadius="borderRadius"
              $iconSize="M"
              $color="red"
              $background="redBackground"
              icon="trash"
              onClick={onDelete}
              ariaLabel="Delete"
            />
          </ActionsRow>
        </Row>

        {expanded && scraping.scraped_urls.items.length > 0 && (
          <ExpandedSection $marginTop="S" $paddingTop="S">
            <Label $marginBottom="XS">{t("profile.knowledge-base.scrapings.scraped-urls")}</Label>
            <URLList>
              {scraping.scraped_urls.items.map(url => (
                <URLItem key={url.id}>
                  <P $small $marginBottom="0">
                    <a href={url.url} target="_blank" rel="noreferrer">
                      {url.url}
                    </a>
                  </P>
                  <P $small $marginBottom="0">
                    {formatDate(url.scraped_at, i18n.language)}
                  </P>
                </URLItem>
              ))}
            </URLList>
            {scraping.scraped_urls.total > scraping.scraped_urls.items.length && (
              <P $small $marginTop="XS" $marginBottom="0">
                {t("profile.knowledge-base.scrapings.showing", {
                  showing: scraping.scraped_urls.items.length,
                  total: scraping.scraped_urls.total,
                })}
              </P>
            )}
          </ExpandedSection>
        )}
      </Box>
    </ScrapingWrapper>
  )
}

const ProfileKnowledgeBaseScrapingsView = () => {
  const { t } = useTranslation(["panel", "common"])
  const { profile } = useSelector((state: RootState) => state.profile)
  const [scrapings, setScrapings] = useState<DomainScrapingInfo[]>([])
  const [loading, setLoading] = useState(false)
  const [scraping, setScraping] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [domain, setDomain] = useState("")
  const [scrapingProgress, setScrapingProgress] = useState<string | null>(null)

  const fetchScrapings = useCallback(async () => {
    if (!profile?.id) return

    try {
      setLoading(true)
      setError(null)
      const data = await KnowledgeBaseService.getScrapings()
      setScrapings(data)
    } catch (e) {
      SentryService.error("[ERROR KnowledgeBaseService.getScrapings]:", e)
      setError(t("profile.knowledge-base.scrapings.error.load"))
    } finally {
      setLoading(false)
    }
  }, [profile?.id, t])

  useEffect(() => {
    fetchScrapings()
  }, [fetchScrapings])

  const handleScrape = useCallback(async () => {
    if (!profile?.id || !domain.trim()) return

    try {
      setScraping(true)
      setError(null)
      setScrapingProgress(t("profile.knowledge-base.scrapings.scraping-in-progress"))
      const data: ScrapeDomainRequest = {
        domain: domain.trim(),
        max_pages: 99,
      }
      await KnowledgeBaseService.scrapeDomain(data)
      setScrapingProgress(null)
      setDomain("")
      fetchScrapings()
      window.alert(t("profile.knowledge-base.scrapings.success"))
    } catch (e) {
      SentryService.error("[ERROR KnowledgeBaseService.scrapeDomain]:", e)
      setError(t("profile.knowledge-base.scrapings.error.scrape"))
      setScrapingProgress(null)
    } finally {
      setScraping(false)
    }
  }, [profile?.id, domain, fetchScrapings, t])

  const handleDelete = useCallback(
    async (domain: string) => {
      if (!profile?.id) return

      const confirmMessage = t("profile.knowledge-base.scrapings.confirm-delete")
      if (!window.confirm(confirmMessage)) {
        return
      }

      try {
        setLoading(true)
        setError(null)
        await KnowledgeBaseService.deleteDomain(domain)
        await fetchScrapings()
        window.alert(t("profile.knowledge-base.scrapings.delete-success"))
      } catch (e) {
        SentryService.error("[ERROR KnowledgeBaseService.deleteDomain]:", e)
        setError(t("profile.knowledge-base.scrapings.error.delete"))
      } finally {
        setLoading(false)
      }
    },
    [profile?.id, fetchScrapings, t],
  )

  const handleRefresh = useCallback(
    async (domainUrl: string) => {
      if (!profile?.id) return

      try {
        setScraping(true)
        setScrapingProgress(t("profile.knowledge-base.scrapings.scraping-in-progress"))
        const data: ScrapeDomainRequest = {
          domain: domainUrl,
          max_pages: 99,
        }
        await KnowledgeBaseService.scrapeDomain(data)
        setScrapingProgress(null)
        fetchScrapings()
      } catch (e) {
        SentryService.error("[ERROR KnowledgeBaseService.scrapeDomain]:", e)
        setError(t("profile.knowledge-base.scrapings.error.scrape"))
        setScrapingProgress(null)
      } finally {
        setScraping(false)
      }
    },
    [profile?.id, fetchScrapings, t],
  )

  const isValidURL = (url: string) => {
    try {
      const fullUrl = url.startsWith("http") ? url : `https://${url}`
      const urlObj = new URL(fullUrl)
      const hostname = urlObj.hostname

      // Sprawdź czy hostname istnieje i nie jest pusty
      if (!hostname || hostname.length === 0) {
        return false
      }

      // Sprawdź czy hostname ma co najmniej jedną kropkę (domena musi mieć TLD)
      if (!hostname.includes(".")) {
        return false
      }

      // Sprawdź czy hostname nie jest tylko IP (opcjonalnie można dodać walidację IP)
      // Ale na razie skupiamy się na domenach

      // Regex dla poprawnej domeny:
      // - Musi zawierać co najmniej jedną kropkę
      // - Każda część domeny może zawierać litery, cyfry i myślniki
      // - Nie może zaczynać ani kończyć się myślnikiem lub kropką
      // - TLD musi mieć co najmniej 2 znaki
      const domainRegex =
        /^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/i

      if (!domainRegex.test(hostname)) {
        return false
      }

      // Sprawdź czy TLD ma co najmniej 2 znaki
      const parts = hostname.split(".")
      const tld = parts[parts.length - 1]
      if (tld.length < 2) {
        return false
      }

      return true
    } catch {
      return false
    }
  }

  return (
    <Wrapper>
      <Box $marginBottom="S">
        <Title $marginBottom="XS">{t("profile.knowledge-base.scrapings.title")}</Title>
        <P $small $marginBottom="0">
          {t("profile.knowledge-base.scrapings.text")}
        </P>
      </Box>

      {error && (
        <Alert $type="error" $marginBottom="S">
          {error}
        </Alert>
      )}

      {scrapingProgress && (
        <Alert $type="warning" $marginBottom="S">
          {scrapingProgress}
        </Alert>
      )}

      <AddFormWrapper $marginBottom="L">
        <Box $flex={1}>
          <Input
            name="domain"
            label={t("profile.knowledge-base.scrapings.domain-url")}
            placeholder="https://example.com"
            value={domain}
            onChange={({ name, value }) => setDomain(value as string)}
          />
          {domain && !isValidURL(domain) && (
            <ErrorText>{t("profile.knowledge-base.scrapings.invalid-url")}</ErrorText>
          )}
        </Box>
        <Button
          icon="plus"
          onClick={handleScrape}
          loading={scraping}
          disabled={!domain.trim() || !isValidURL(domain)}
        >
          {t("profile.knowledge-base.scrapings.start-scraping")}
        </Button>
      </AddFormWrapper>

      <Content>
        {loading && (
          <Row $justifyContent="center">
            <Spinner $size="30px" />
          </Row>
        )}

        {!loading && scrapings.length === 0 && (
          <P $small $marginBottom="0">
            {t("profile.knowledge-base.scrapings.empty")}
          </P>
        )}

        {!loading &&
          scrapings.map(scraping => (
            <ScrapingCard
              key={scraping.id}
              scraping={scraping}
              onRefresh={() => handleRefresh(scraping.domain)}
              onDelete={() => handleDelete(scraping.domain)}
            />
          ))}
      </Content>
    </Wrapper>
  )
}

export default ProfileKnowledgeBaseScrapingsView

const ScrapingWrapper = styled(Box)`
  padding: ${({ theme }) => theme.space.S};
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.variable.borderRadius};
`

const AddFormWrapper = styled(Box)`
  padding: ${({ theme }) => theme.space.S};
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.variable.borderRadius};
  display: flex;
  flex-direction: column;
  grid-gap: ${({ theme }) => theme.space.S};
`

const URLList = styled(Box)`
  display: flex;
  flex-direction: column;
  grid-gap: ${({ theme }) => theme.space.XS};
  max-height: 400px;
  overflow-y: auto;
`

const ExpandedSection = styled(Box)`
  border-top: 1px solid ${({ theme }) => theme.color.border};
`

const ActionsRow = styled(Row)`
  grid-gap: ${({ theme }) => theme.space.XS};
`

const URLItem = styled(Box)`
  padding: ${({ theme }) => theme.space.XS};
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${({ theme }) => theme.color.background};
  border-radius: ${({ theme }) => theme.variable.borderRadius};
`

const Content = styled(Box)`
  width: 100%;
  display: flex;
  flex-direction: column;
  grid-gap: ${({ theme }) => theme.space.S};
`

const Wrapper = styled(Box)`
  padding: ${({ theme }) => theme.space.M} 0;
`

const ErrorText = styled(P)`
  margin-bottom: 0;
  margin-top: ${({ theme }) => theme.space.XS};
  color: ${({ theme }) => theme.color.red} !important;
  font-size: ${({ theme }) => theme.font.S};
`
